import Header from '@/components/Header';
import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Link } from 'react-router-dom';
import { SeverityChip } from '@/components/SeverityChip';
import { AlertsMap } from '@/components/AlertsMap';
import { Bell, Settings as Cog, AlertOctagon, TrendingUp, ChevronRight, Route as RouteIcon, CloudRain, AlertTriangle, CheckCircle2, Zap, Waves } from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [myAlerts, setMyAlerts] = useState([]);
  const [mapAlerts, setMapAlerts] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [journeyLegs, setJourneyLegs] = useState([]);
  const [quakes, setQuakes] = useState([]);
  const [showQuakes, setShowQuakes] = useState(false);
  const isBusiness = user?.role === 'admin' || user?.plan === 'business';

  useEffect(() => {
    api.get('/alerts/stats').then((r) => setStats(r.data));
    api.get('/notifications?limit=8').then((r) => setNotifications(r.data));
    api.get('/alerts?status_filter=Active').then((r) => setMapAlerts(r.data));
    api.get('/routes').then((r) => setRoutes(r.data)).catch(() => {});
    let jv;
    if (user?.role === 'admin' || user?.plan === 'business') {
      const fetchJ = () => api.get('/journeys/my').then((r) => setJourneyLegs(r.data || [])).catch(() => {});
      fetchJ();
      jv = setInterval(fetchJ, 60000);
      api.get('/quakes?limit=50').then((r) => setQuakes(r.data || [])).catch(() => {});
    }
    const subs = user?.subscribed_highways || [];
    if (subs.length === 0) {
      // show recent Red/Orange from all
      api.get('/alerts?severity=red&limit=6').then((r) => setMyAlerts(r.data));
    } else {
      // fetch alerts for each subscribed highway_key (client join, small dataset)
      api.get('/alerts').then((r) => {
        setMyAlerts(r.data.filter((a) => a.highway_key && subs.includes(a.highway_key)).slice(0, 12));
      });
    }
    return () => { if (jv) clearInterval(jv); };
  }, [user]);

  const threshold = user?.alert_threshold || 'yellow';
  const channels = user?.channels || {};

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="mb-8">
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500">Welcome back</div>
          <h1 className="font-display font-black text-3xl md:text-4xl">Kia ora, {user?.full_name?.split(' ')[0] || user?.email.split('@')[0]}</h1>
        </div>

        {/* Can I drive? — worst status across the user's corridors */}
        <TrialBanner user={user} />
        <CommuteBanner routes={routes} subs={user?.subscribed_highways || []} alerts={mapAlerts} />

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="Critical (Red)" value={stats?.by_severity?.red ?? '—'} tint="#E11D48" icon={<AlertOctagon className="w-5 h-5" />} />
          <StatCard label="Urgent (Orange)" value={stats?.by_severity?.orange ?? '—'} tint="#F7941D" icon={<TrendingUp className="w-5 h-5" />} />
          <StatCard label="Info (Yellow)" value={stats?.by_severity?.yellow ?? '—'} tint="#FBBF24" icon={<Bell className="w-5 h-5" />} />
          <StatCard label="Highways Affected" value={stats?.highways_affected ?? '—'} tint="#2DD4B0" icon={<TrendingUp className="w-5 h-5" />} />
        </div>

        {/* Live map */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <h2 className="font-display font-bold text-xl">Live event map</h2>
            <div className="flex items-center gap-3">
              {isBusiness && quakes.length > 0 && (
                <button onClick={() => setShowQuakes((s) => !s)} data-testid="dash-quake-toggle"
                  className={`chip inline-flex items-center gap-1.5 text-sm transition-colors ${showQuakes ? 'text-white' : 'text-zinc-500 bg-zinc-100'}`}
                  style={showQuakes ? { background: '#7c3aed' } : {}}>
                  <Waves className="w-4 h-4" /> Earthquakes
                </button>
              )}
              <span className="text-xs text-zinc-500">{mapAlerts.filter((a) => a.lat && a.lng).length} located events</span>
            </div>
          </div>
          <AlertsMap alerts={mapAlerts} quakes={showQuakes ? quakes : []} height={360} />
        </div>

        {/* Trip Watch — combined road + weather per corridor */}
        <CorridorWatch routes={routes} subs={user?.subscribed_highways || []} alerts={mapAlerts} />

        {isBusiness && <JourneyBoard legs={journeyLegs} />}

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 min-w-0">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-xl">
                {(user?.subscribed_highways || []).length ? 'Your highways' : 'Latest critical alerts'}
              </h2>
              <Link to="/alerts" className="text-sm text-orange-600 font-semibold hover:underline">See all →</Link>
            </div>
            <div className="grid gap-3">
              {myAlerts.length === 0 && (
                <div className="card text-center py-10">
                  <p className="text-zinc-500 mb-3">
                    {(user?.subscribed_highways || []).length
                      ? "No active alerts on your subscribed highways."
                      : "You haven't subscribed to any highways yet."}
                  </p>
                  <Link to="/settings" className="btn btn-secondary">Manage highways</Link>
                </div>
              )}
              {myAlerts.map((a) => (
                <Link key={a.id} to={`/alerts/${a.id}`} className="card card-hover flex items-center gap-4 min-w-0" data-testid={`dash-alert-${a.id}`}>
                  <SeverityChip severity={a.severity} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{a.event_type} · <span className="text-zinc-500">{a.highway || 'NZ'}</span></div>
                    <div className="text-xs text-zinc-500 truncate">{a.location_area}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-zinc-400" />
                </Link>
              ))}
            </div>
          </div>

          <aside className="space-y-6 min-w-0">
            <div className="card" data-testid="dash-routes">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold text-lg flex items-center gap-2"><RouteIcon className="w-4 h-4 text-teal-600" /> Your Routes</h3>
                <Link to="/settings" className="text-orange-600 text-sm font-semibold hover:underline">Manage →</Link>
              </div>
              {routes.length === 0 ? (
                <p className="text-sm text-zinc-500">No saved routes yet. Create a route (your commute or a freight lane) in Settings to track just the highways you travel.</p>
              ) : (
                <div className="space-y-2">
                  {routes.map((r) => {
                    const active = mapAlerts.filter((a) => alertOnRoute(a, r));
                    const reds = active.filter((a) => a.severity === 'red').length;
                    return (
                      <div key={r.id} className="flex items-center justify-between p-2.5 rounded-lg border border-zinc-100" data-testid={`dash-route-${r.id}`}>
                        <div className="min-w-0">
                          <div className="font-semibold text-sm truncate">{r.name}</div>
                          <div className="text-xs text-zinc-500">{r.highway_keys.length} highways</div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {reds > 0 && <span className="chip chip-red text-[10px]">{reds} red</span>}
                          <span className={`text-sm font-bold ${active.length ? 'text-orange-600' : 'text-teal-600'}`}>{active.length}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold text-lg">Your Preferences</h3>
                <Link to="/settings" className="text-orange-600 text-sm font-semibold flex items-center gap-1 hover:underline"><Cog className="w-3.5 h-3.5" /> Edit</Link>
              </div>
              <div className="space-y-3 text-sm">
                <PrefRow k="Alert threshold" v={<SeverityChip severity={threshold} outline />} />
                <PrefRow k="Highways" v={(user?.subscribed_highways || []).length ? user.subscribed_highways.map((k) => k.replace('N-', '').replace('S-', '') + (k.startsWith('N-') ? '·N' : '·S')).join(', ') : 'None yet'} />
                <PrefRow k="Portal" v={<Toggle on={channels.portal !== false} />} />
                <PrefRow k="Push" v={<Toggle on={!!channels.webpush} />} />
                <PrefRow k="Email" v={<Toggle on={channels.email !== false} />} />
                <PrefRow k="SMS" v={<Toggle on={!!channels.sms} label={user?.sms_active ? 'Active' : 'Paid'} />} />
              </div>
            </div>

            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold text-lg">Recent Notifications</h3>
                <Link to="/notifications" className="text-orange-600 text-sm font-semibold hover:underline">All →</Link>
              </div>
              {notifications.length === 0 && <p className="text-sm text-zinc-500">Nothing yet — you'll get pinged when alerts match your threshold.</p>}
              <div className="space-y-2">
                {notifications.slice(0, 5).map((n) => (
                  <div key={n.id} className={`p-2 rounded-lg text-xs ${n.read ? 'bg-zinc-50' : 'bg-teal-50 border border-teal-200'}`}>
                    <div className="font-semibold text-zinc-800">{n.title}</div>
                    <div className="text-zinc-500 mt-0.5">{dayjs(n.created_at).fromNow()}</div>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function TrialBanner({ user }) {
  const plan = user?.plan || 'free';
  if (!user || user.role === 'admin' || plan !== 'free') return null;
  if (user.trial_active) {
    const left = user.trial_ends_at ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - Date.now()) / 86400000)) : 0;
    return (
      <Link to="/settings" className="mb-6 rounded-xl px-4 py-3 bg-teal-50 border border-teal-200 text-teal-800 text-sm font-medium flex items-center gap-2 hover:bg-teal-100 transition-colors" data-testid="dash-trial-active">
        <Zap className="w-4 h-4 shrink-0" /> Plus trial active — {left} day{left !== 1 ? 's' : ''} left{user.trial_includes_sms ? ' (incl. SMS)' : ''}. <span className="underline">Choose a plan →</span>
      </Link>
    );
  }
  if (!user.trial_used) {
    return (
      <Link to="/settings" className="mb-6 rounded-xl px-4 py-3 bg-orange-50 border border-orange-200 text-orange-800 text-sm font-semibold flex items-center gap-2 hover:bg-orange-100 transition-colors" data-testid="dash-trial-offer">
        <Zap className="w-4 h-4 shrink-0" /> Try Plus free for 7 days — Route Watch, unlimited highways &amp; more. <span className="underline">Start free →</span>
      </Link>
    );
  }
  return null;
}

function fmtTime(sec) {
  const m = Math.round((sec || 0) / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function JourneyBoard({ legs }) {
  if (!legs || legs.length === 0) return null;
  const top = legs.slice(0, 12);
  return (
    <div className="mb-8" data-testid="journey-board">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-bold text-xl flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-teal-600" /> Live travel times
          <span className="text-[10px] font-bold uppercase tracking-wider bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">Business</span>
        </h2>
        <span className="text-xs text-zinc-500">Your highways · Waka Kotahi · updates each minute</span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {top.map((l) => {
          const delayed = (l.delay_s || 0) >= 120;
          return (
            <div key={l.id} className="card flex items-center justify-between gap-3 py-3" data-testid={`journey-leg-${l.id}`}>
              <div className="min-w-0">
                <div className="text-xs font-bold text-teal-700">{l.highway}{l.direction ? ` · ${l.direction}` : ''}</div>
                <div className="text-sm font-semibold truncate">{l.leg_name || l.journey_name}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-display font-black text-lg tabular-nums leading-none">{fmtTime(l.current_s)}</div>
                {delayed
                  ? <div className="text-xs font-semibold text-red-600">+{Math.round(l.delay_s / 60)} min</div>
                  : <div className="text-xs font-semibold text-teal-600">On time</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatCard({ label, value, tint, icon }) {  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs tracking-[0.15em] uppercase font-semibold text-zinc-500">{label}</div>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${tint}18`, color: tint }}>{icon}</div>
      </div>
      <div className="font-display font-black text-4xl" style={{ color: tint }}>{value}</div>
    </div>
  );
}

function PrefRow({ k, v }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span className="text-zinc-500 text-xs uppercase tracking-wider">{k}</span>
      <span className="text-zinc-900 text-right truncate max-w-[60%]">{v}</span>
    </div>
  );
}

function Toggle({ on, label }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${on ? 'text-teal-700' : 'text-zinc-400'}`}>
      <span className={`w-2 h-2 rounded-full ${on ? 'bg-teal-500' : 'bg-zinc-300'}`}></span>
      {label || (on ? 'On' : 'Off')}
    </span>
  );
}

import { pointNearPolyline, alertOnRoute } from '@/lib/geo';

const SEV_ORDER = { yellow: 1, orange: 2, red: 3 };
const SEV_HEX = { red: '#E11D48', orange: '#F7941D', yellow: '#FBBF24' };
const notResolved = (a) => !(a.status || '').toLowerCase().startsWith('resolved');
const shortKey = (k) => `${k.replace('N-', '').replace('S-', '')}·${k[0] === 'N' ? 'N' : 'S'}`;

function corridorStatus(corridor, alerts) {
  const keys = corridor.keys || [];
  const geom = corridor.geometry;
  const islands = new Set(keys.map((k) => (k[0] === 'N' ? 'north' : 'south')));
  const onRoad = (a) => {
    if (a.highway_key) {
      if (!keys.includes(a.highway_key)) return false;
      if (geom && geom.length && a.lat != null && a.lng != null) return pointNearPolyline(a.lat, a.lng, geom, corridor.buffer_km || 8);
      return true;
    }
    // Local-council (e.g. Auckland) events: no highway key — geo-fence to the drive only.
    if (geom && geom.length && a.lat != null && a.lng != null) return pointNearPolyline(a.lat, a.lng, geom, corridor.buffer_km || 8);
    return false;
  };
  const road = alerts.filter((a) => notResolved(a) && a.source !== 'metservice' && onRoad(a));
  const weather = alerts.filter((a) => notResolved(a) && a.source === 'metservice' && a.island && islands.has(a.island));
  const worst = [...road, ...weather].reduce((w, a) => (SEV_ORDER[a.severity] > (SEV_ORDER[w] || 0) ? a.severity : w), null);
  return { road, weather, worst };
}

function CommuteBanner({ routes, subs, alerts }) {
  const corridors = [...routes.map((r) => ({ id: r.id, keys: r.highway_keys || [], geometry: r.geometry, buffer_km: r.buffer_km }))];
  if (!corridors.length && subs.length) corridors.push({ id: null, keys: subs });
  if (!corridors.length) return null;
  let worst = null, total = 0, reds = 0, worstId = corridors[0].id;
  corridors.forEach((c) => {
    const { road, weather } = corridorStatus(c, alerts);
    const all = [...road, ...weather];
    total += all.length;
    reds += all.filter((a) => a.severity === 'red').length;
    all.forEach((a) => { if (SEV_ORDER[a.severity] > (SEV_ORDER[worst] || 0)) { worst = a.severity; worstId = c.id; } });
  });
  const clear = total === 0;
  const cfg = clear
    ? { bg: '#0f766e', label: 'Clear to drive', sub: 'No active road events or weather warnings on your routes right now.' }
    : worst === 'red'
    ? { bg: '#E11D48', label: 'Major disruption', sub: `${reds} critical closure${reds > 1 ? 's' : ''} on your routes — check before you travel.` }
    : worst === 'orange'
    ? { bg: '#B45309', label: 'Expect delays', sub: `${total} active event${total > 1 ? 's' : ''} on your routes — allow extra time.` }
    : { bg: '#A16207', label: 'Minor advisories', sub: `${total} advisory${total > 1 ? ' events' : ''} on your routes.` };
  const to = worstId ? `/alerts?route=${worstId}` : '/alerts';
  return (
    <Link to={to} className="block mb-8 rounded-2xl p-5 text-white shadow-sm hover:opacity-95 transition-opacity" style={{ background: cfg.bg }} data-testid="commute-banner">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">{clear ? <CheckCircle2 className="w-6 h-6" /> : <AlertTriangle className="w-6 h-6" />}</div>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.2em] opacity-80">Can I drive?</div>
          <div className="font-display font-black text-2xl leading-tight" data-testid="commute-banner-label">{cfg.label}</div>
          <div className="text-sm opacity-90">{cfg.sub}</div>
        </div>
        <ChevronRight className="w-6 h-6 ml-auto opacity-80 shrink-0" />
      </div>
    </Link>
  );
}

function CorridorWatch({ routes, subs, alerts }) {
  // Build the list of corridors to watch: saved routes, plus a fallback "My highways" bundle.
  const corridors = [...routes.map((r) => ({ id: r.id, name: r.name, keys: r.highway_keys || [], geometry: r.geometry, buffer_km: r.buffer_km }))];
  if (corridors.length === 0 && subs.length) corridors.push({ id: 'subs', name: 'My highways', keys: subs });
  if (corridors.length === 0) return null;

  return (
    <div className="mb-8" data-testid="corridor-watch">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display font-bold text-xl flex items-center gap-2">
            <RouteIcon className="w-5 h-5 text-teal-600" /> Trip Watch — road + weather
          </h2>
          <p className="text-sm text-zinc-500">The full A→B picture on your corridors: NZTA road events and MetService weather, combined.</p>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {corridors.map((c) => {
          const { road, weather, worst } = corridorStatus(c, alerts);
          const clear = !road.length && !weather.length;
          const tint = worst ? SEV_HEX[worst] : '#2DD4B0';
          const headlineBits = [];
          if (road.some((a) => (a.impact || '').toLowerCase().includes('closed') || (a.event_type || '').toLowerCase().includes('closed'))) headlineBits.push('Road Closed');
          else if (road.length) headlineBits.push(`${road.length} road event${road.length > 1 ? 's' : ''}`);
          const w0 = weather[0];
          if (w0) headlineBits.push(w0.event_type);
          return (
            <div key={c.id} className="card min-w-0" style={{ borderLeft: `4px solid ${tint}` }} data-testid={`corridor-card-${c.id}`}>
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="font-display font-bold text-base truncate min-w-0 flex-1">{c.name}</div>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold flex-shrink-0" style={{ color: tint }}>
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: tint }} />
                  {clear ? 'All clear' : (headlineBits.join(' + ') || 'Advisory')}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {c.keys.slice(0, 8).map((k) => <span key={k} className="chip chip-outline-teal text-[10px]">{shortKey(k)}</span>)}
                {c.keys.length > 8 && <span className="text-[10px] text-zinc-400 self-center">+{c.keys.length - 8}</span>}
              </div>
              {clear ? (
                <div className="text-sm text-emerald-600 font-medium">No active road events or weather warnings on this corridor.</div>
              ) : (
                <div className="space-y-1.5">
                  {road.slice(0, 3).map((a) => (
                    <Link key={a.id} to={`/alerts/${a.id}`} className="flex items-center gap-2 text-sm hover:underline min-w-0" data-testid={`corridor-road-${a.id}`}>
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: SEV_HEX[a.severity] }} />
                      <AlertOctagon className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" />
                      <span className="truncate min-w-0 flex-1">{a.event_type} · <span className="text-zinc-500">{a.highway || 'NZ'} {a.location_area}</span></span>
                    </Link>
                  ))}
                  {weather.slice(0, 3).map((a) => (
                    <Link key={a.id} to={`/alerts/${a.id}`} className="flex items-center gap-2 text-sm hover:underline min-w-0" data-testid={`corridor-weather-${a.id}`}>
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: SEV_HEX[a.severity] }} />
                      <CloudRain className="w-3.5 h-3.5 text-sky-500 flex-shrink-0" />
                      <span className="truncate min-w-0 flex-1">{a.event_type} · <span className="text-zinc-500">{a.location_area || (a.island === 'north' ? 'North Is.' : 'South Is.')}</span></span>
                    </Link>
                  ))}
                  {(road.length > 3 || weather.length > 3) && (
                    <div className="text-xs text-zinc-400">+{road.length + weather.length - Math.min(road.length, 3) - Math.min(weather.length, 3)} more</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
